import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { useGoBack } from "../../../hooks/useGoBack";
import {
  ADJUSTMENT_REASON_LABELS,
  ADJUSTMENT_TYPE_LABELS,
  AdjustmentStatus,
  cancelMyAdjustmentApi,
  duration,
  getMyAdjustmentsApi,
  MyAdjustment,
  timeOf,
} from "../api/timekeepingApi";
import EmployeeShell from "../components/EmployeeShell";
import { COLABORADOR_ESPELHO } from "../routes";

import "./Colaborador.css";
import "./Ponto.css";

const STATUS: Record<AdjustmentStatus, { label: string; tone: string }> = {
  PENDENTE: { label: "Aguardando o DP", tone: "colabBadge--orange" },
  APROVADO: { label: "Aprovado", tone: "colabBadge--green" },
  REJEITADO: { label: "Recusado", tone: "colabBadge--gray" },
};

const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", { timeZone: "UTC", weekday: "short", day: "2-digit", month: "2-digit" });

/** Um pedido (ou um lote do mesmo dia) em uma linha legível. */
function describe(group: MyAdjustment[]) {
  const first = group[0];
  if (first.type === "INCLUSAO_MARCACAO") {
    const times = group.filter((a) => a.proposedAt).map((a) => timeOf(a.proposedAt as string));
    return `${group.length > 1 ? "Incluir marcações" : "Incluir marcação"}: ${times.join(", ")}`;
  }
  if (first.type === "DESCONSIDERAR_MARCACAO") {
    return `Desconsiderar a marcação${first.targetPunch ? ` das ${timeOf(first.targetPunch.punchedAt)}` : ""}`;
  }
  if (first.minutes) return `${ADJUSTMENT_TYPE_LABELS[first.type]}: ${duration(first.minutes)}`;
  return ADJUSTMENT_TYPE_LABELS[first.type];
}

/** Os pedidos de correção do ponto e o que o DP decidiu. */
export default function MyAdjustmentsPage() {
  const goBack = useGoBack(COLABORADOR_ESPELHO());
  const [items, setItems] = useState<MyAdjustment[] | null>(null);
  const [filter, setFilter] = useState<AdjustmentStatus | "TODOS">("TODOS");
  const [cancelIds, setCancelIds] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(await getMyAdjustmentsApi());
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível carregar os seus pedidos.");
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Lote (dia inteiro) vira um cartão só.
  const groups = useMemo(() => {
    const map = new Map<string, MyAdjustment[]>();
    for (const a of items ?? []) {
      const key = a.batchId ?? a.id;
      map.set(key, [...(map.get(key) ?? []), a]);
    }
    return Array.from(map.values()).filter((g) => filter === "TODOS" || g[0].status === filter);
  }, [items, filter]);

  const pending = (items ?? []).filter((a) => a.status === "PENDENTE").length;

  const cancel = async () => {
    if (!cancelIds) return;
    setBusy(true);
    try {
      for (const id of cancelIds) await cancelMyAdjustmentApi(id);
      toast.success("Pedido cancelado.");
      setCancelIds(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível cancelar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <EmployeeShell>
      <section className="colabPanel">
        <div className="colabPageTitle">
          <h2 className="colabPanel__title" style={{ margin: 0 }}>
            Meus pedidos de correção
          </h2>
          <button type="button" className="colabBtn colabBtn--ghost colabBtn--compact" onClick={goBack}>
            Voltar
          </button>
        </div>

        <div className="colabSegmented" role="group" aria-label="Situação">
          {(["TODOS", "PENDENTE", "APROVADO", "REJEITADO"] as const).map((s) => (
            <button key={s} type="button" className={filter === s ? "is-on" : ""} onClick={() => setFilter(s)}>
              {s === "TODOS" ? "Todos" : s === "PENDENTE" ? `Aguardando (${pending})` : STATUS[s].label}
            </button>
          ))}
        </div>

        {items === null ? (
          <p className="colabLoading">Carregando...</p>
        ) : !groups.length ? (
          <p className="colabHint">Nenhum pedido. Para pedir uma correção, abra o seu espelho e toque em "Corrigir" no dia.</p>
        ) : (
          <div className="colabList">
            {groups.map((group) => {
              const first = group[0];
              const status = STATUS[first.status];
              const annulled = group.every((a) => a.annulledBy);
              return (
                <article key={first.batchId ?? first.id} className="colabListItem">
                  <div className="colabListItem__head">
                    <span>{formatDay(first.day)}</span>
                    <span className={`colabBadge ${annulled ? "colabBadge--gray" : status.tone}`}>{annulled ? "Anulado pelo DP" : status.label}</span>
                  </div>
                  <p className="colabListItem__meta" style={{ color: "var(--eco-ink)" }}>
                    {describe(group)}
                  </p>
                  <p className="colabListItem__meta">
                    {ADJUSTMENT_REASON_LABELS[first.reasonCode]}
                    {first.reason ? ` · ${first.reason}` : ""}
                    {!first.requestedByEmployee ? " · lançado pelo DP" : ""}
                  </p>
                  {first.decisionNote ? <p className="colabListItem__meta">DP: {first.decisionNote}</p> : null}
                  {first.status === "PENDENTE" && first.requestedByEmployee ? (
                    <button
                      type="button"
                      className="colabBtn colabBtn--ghost colabBtn--compact"
                      style={{ marginTop: 8 }}
                      onClick={() => setCancelIds(group.filter((a) => a.status === "PENDENTE").map((a) => a.id))}
                    >
                      Cancelar pedido
                    </button>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </section>

      <EcoConfirmDialog
        open={Boolean(cancelIds)}
        title="Cancelar o pedido?"
        message="O DP deixa de ver este pedido. Você pode fazer outro depois, pelo espelho."
        confirmLabel="Cancelar pedido"
        tone="danger"
        busy={busy}
        onConfirm={cancel}
        onCancel={() => setCancelIds(null)}
      />
    </EmployeeShell>
  );
}
